import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaStar } from 'react-icons/fa';
import { FiArrowRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { fetchAPI } from '../../utils/api';

const Feedback = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const getFeedback = async () => {
            try {
                const data = await fetchAPI('/public/get_feedback.php');
                setTestimonials(data);
            } catch (error) {
                console.error('Error fetching feedback:', error);
            } finally {
                setLoading(false);
            }
        };
        getFeedback();
    }, []);

    const truncateWords = (text, limit) => {
        if (!text) return '';
        const words = text.split(/\s+/);
        if (words.length <= limit) return text;
        return words.slice(0, limit).join(' ') + '...';
    };

    return (
        <section className="py-20 px-6 bg-[#0a0a0a]" id="feedback">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-center gap-4 mb-3"
                    >
                        <div className="h-[1px] w-6 bg-blue-500"></div>
                        <span className="text-blue-500 font-bold uppercase tracking-widest text-[10px]">Success Stories</span>
                        <div className="h-[1px] w-6 bg-blue-500"></div>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-2xl md:text-4xl font-black tracking-tight text-white uppercase"
                    >
                        STUDENT <span className="text-blue-500">FEEDBACK</span>
                    </motion.h2>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="w-8 h-8 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
                    </div>
                ) : testimonials.length > 0 ? (
                    <div className="space-y-12">
                        <div className="flex flex-wrap justify-center gap-6">
                            {testimonials.slice(0, 3).map((t, idx) => {
                                const isLong = t.content && t.content.split(/\s+/).length > 30;
                                return (
                                    <motion.div
                                        key={t.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ delay: (idx % 3) * 0.1 }}
                                        viewport={{ once: true }}
                                        className="bg-white/[0.03] backdrop-blur-xl p-8 rounded-3xl flex flex-col justify-between w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-1.5rem)] border border-white/5 hover:border-blue-500/30 transition-all duration-500 group relative overflow-hidden"
                                    >
                                        {/* Decorative Quote Mark */}
                                        <div className="absolute top-6 right-8 text-5xl font-serif text-white/[0.02] group-hover:text-blue-500/[0.05] transition-colors pointer-events-none">"</div>

                                        <div className="mb-8 relative z-10">
                                            <div className="flex gap-1 text-blue-500 mb-6">
                                                {[...Array(5)].map((_, i) => <FaStar key={i} className="text-[10px]" />)}
                                            </div>
                                            <p className="text-base text-white/70 font-medium italic leading-relaxed group-hover:text-white/90 transition-colors">
                                                "{truncateWords(t.content, 30)}"
                                            </p>
                                            {isLong && (
                                                <button
                                                    onClick={() => navigate('/feedback')}
                                                    className="mt-4 text-[10px] font-black uppercase tracking-widest text-blue-500 hover:text-blue-400 transition-colors flex items-center gap-2"
                                                >
                                                    Read Full Story <FiArrowRight />
                                                </button>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-4 pt-6 border-t border-white/5 relative z-10 mt-auto">
                                            <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center font-black text-lg text-blue-500 shadow-2xl group-hover:scale-110 transition-transform">
                                                {t.author ? t.author[0] : 'S'}
                                            </div>
                                            <div>
                                                <h4 className="font-black text-white text-sm mb-0.5 uppercase tracking-tight">{t.author}</h4>
                                                <span className="text-[9px] font-bold text-blue-500 uppercase tracking-widest">{t.tags?.join(' • ') || 'Student'}</span>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* View All Button */}
                        <div className="flex justify-center pt-8">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate('/feedback')}
                                className="px-10 py-4 bg-white/5 text-white border border-white/10 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-blue-600 transition-all group flex items-center gap-3"
                            >
                                Explorer All Feedback <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                            </motion.button>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-20 text-white/30 font-bold uppercase tracking-[0.3em] text-xs">
                        No feedback records found in the registry.
                    </div>
                )}
            </div>
        </section>
    );
};

export default Feedback;
